import { RefreshCwIcon } from "lucide-react";
import React from "react";

import cn from "@/helpers/classNames";
import { captureException } from "@sentry/react";

import { Button } from "../elements/Button";
import { Heading } from "../elements/Typography";

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: (error: Error | null) => React.ReactNode;
  report?: boolean | ((error: Error) => boolean);
  className?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    if (this.props.report === false) {
      return;
    }
    if (
      typeof this.props.report === "function" &&
      this.props.report(error) === false
    ) {
      return;
    }
    captureException(error, { extra: errorInfo as any });
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error);
      }
      return (
        <div
          className={cn(
            "inline-flex flex-col rounded-sm bg-red-50 px-4 py-3 dark:bg-red-900",
            this.props.className,
          )}
        >
          <div>
            <Heading className="text-base text-red-600 dark:text-red-400">
              Something went wrong
            </Heading>
            <p className="mt-1 text-sm">{this.state.error?.message}</p>
          </div>
          <div className="mt-2 flex w-full shrink justify-center">
            <Button
              onClick={() => window.location.reload()}
              size="xs"
              icon={<RefreshCwIcon />}
            >
              Reload page
            </Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
